import { Dialog, DialogFooter, Icon, Label, Text } from "@fluentui/react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { FormSpinner } from "../../components/FormSpinner";
import { Flex, HSpace, StyledPrimaryButton } from "../../components/styled";
import { Select } from "../../components/Select";
import { useSelector } from "../../store/hooks";
import {
  changeRecommendationReportRoutine,
  setChangeReportDialog,
} from "../../store/recommendations-page/recommendationsPage";
import { selectAllReports } from "../../store/reports/reports";
import { selectEntitiesLocations } from "../../store/locations/locations";

type ReportOption = {
  label: string;
  value: number;
} | null;

export const ChangeReportDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { recommendation } = useSelector(
    (s) => s.recommendationsPage.changeReportDialog,
  );
  const isLoading = useSelector(
    (s) => s.recommendationsPage.changeReportLoadStatus === "loading",
  );
  const reports = useSelector(selectAllReports);
  const locationEntities = useSelector(selectEntitiesLocations);

  const [report, setReport] = useState<ReportOption>(null);

  const reportOptions = useMemo(() => {
    if (!recommendation) return [];
    const recClientId = Number(recommendation.webBasePartnerNumber);
    const recReportId = Number(recommendation.reportId);
    return reports
      .filter(
        (r) =>
          Number(r.webBasePartnerNumber) === recClientId &&
          Number(r.reportId) !== recReportId &&
          !r.isReportDeactivated,
      )
      .map((r) => {
        const loc = locationEntities[r.locationId];
        return {
          label: loc ? `${r.reportName} (${loc.locationName})` : r.reportName,
          value: r.reportId,
        };
      });
  }, [reports, recommendation, locationEntities]);

  const close = () => {
    dispatch(
      setChangeReportDialog({
        recommendation: null,
        isOpen: false,
      }),
    );
  };

  return (
    <Dialog
      minWidth={700}
      hidden={false}
      dialogContentProps={{
        showCloseButton: true,
        title: (
          <Flex>
            <Icon iconName="Switch" /> <HSpace />
            <div>{t("ram.changeReport.label")}</div>
          </Flex>
        ),
      }}
      onDismiss={close}
    >
      <Text
        block
        styles={{
          root: {
            marginBottom: 10,
          },
        }}
      >
        {t("ram.changeReportDialogDescription.label")}
      </Text>
      <div>
        <Label>{t("ram.reportList.reportName")}</Label>
        <Select
          options={reportOptions}
          value={report}
          onChange={(option: ReportOption) => setReport(option)}
          isClearable
          menuPosition="fixed"
          placeholder={t("greco.form.searchPlaceholder")}
          noOptionsMessage={() => t("greco.noResults")}
        />
      </div>
      <DialogFooter>
        <StyledPrimaryButton
          text={t("ram.changeReport.label")}
          onClick={() => {
            dispatch(
              changeRecommendationReportRoutine.trigger({
                locationId: recommendation.locationId,
                reportId: recommendation.reportId,
                recommendationId: recommendation.recommendationId,
                newReportId: report?.value,
              }),
            );
          }}
          disabled={isLoading || !report?.value}
        />
      </DialogFooter>
      {isLoading && <FormSpinner />}
    </Dialog>
  );
};
